import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, X, SendHorizonal } from "lucide-react";
import { API } from "../lib/api";

const GREETING = {
  role: "assistant",
  content: "Hello — I'm Axiom, the firm's assistant. Ask me about our services, research areas, or how an engagement works.",
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const historyLoaded = useRef(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(
    localStorage.getItem("axiom-chat-session") ||
      (() => {
        const id = crypto.randomUUID();
        localStorage.setItem("axiom-chat-session", id);
        return id;
      })()
  );

  useEffect(() => {
    if (!open || historyLoaded.current) return;
    historyLoaded.current = true;
    fetch(`${API}/chat/history/${sessionId.current}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((docs) => {
        if (Array.isArray(docs) && docs.length > 0) {
          setMessages([GREETING, ...docs.map((d) => ({ role: d.role, content: d.content }))]);
        }
      })
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const appendDelta = (delta) =>
    setMessages((m) => {
      const copy = [...m];
      const last = copy[copy.length - 1];
      copy[copy.length - 1] = { ...last, content: last.content + delta };
      return copy;
    });

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop();
        for (const ev of events) {
          const line = ev.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.delta) appendDelta(parsed.delta);
            if (parsed.error) appendDelta(parsed.error);
          } catch {}
        }
      }
    } catch {
      appendDelta("I'm having trouble connecting. Please try again in a moment.");
    }
    setStreaming(false);
  };

  return (
    <>
      <motion.button
        onClick={() => setOpen(!open)}
        data-testid="chat-widget-button"
        aria-label="Open Axiom assistant"
        className="fixed bottom-6 right-6 z-[80] h-14 w-14 rounded-full bg-ink text-paper shadow-2xl flex items-center justify-center hover:bg-cobalt transition-colors duration-300"
        whileTap={{ scale: 0.92 }}
      >
        {open ? <X size={22} /> : <MessageSquare size={22} />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-[80] w-[min(92vw,384px)] h-[540px] max-h-[72vh] rounded-2xl border border-ink/10 bg-white/85 backdrop-blur-xl shadow-2xl flex flex-col overflow-hidden"
            data-testid="chat-panel"
          >
            <div className="px-5 py-4 border-b border-ink/10 flex items-center gap-3">
              <span className="h-2.5 w-2.5 rounded-full bg-cobalt animate-pulse-dot" />
              <div>
                <p className="font-display text-lg text-ink leading-none">Axiom Assistant</p>
                <p className="text-xs text-ink/50 mt-1">Answers about the firm &amp; its research</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chat-messages-list">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-message-${m.role}`}
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-ink text-paper rounded-br-sm"
                        : "bg-secondary text-ink rounded-bl-sm"
                    }`}
                  >
                    {m.content || (streaming && i === messages.length - 1 ? (
                      <span className="inline-flex gap-1 py-1">
                        <span className="h-1.5 w-1.5 rounded-full bg-ink/40 animate-pulse-dot" />
                        <span className="h-1.5 w-1.5 rounded-full bg-ink/40 animate-pulse-dot [animation-delay:0.2s]" />
                        <span className="h-1.5 w-1.5 rounded-full bg-ink/40 animate-pulse-dot [animation-delay:0.4s]" />
                      </span>
                    ) : null)}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-3 border-t border-ink/10 flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about Axiom…"
                data-testid="chat-input"
                className="flex-1 bg-white border border-ink/10 rounded-full px-4 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-cobalt/50"
              />
              <button
                onClick={send}
                disabled={streaming || !input.trim()}
                data-testid="chat-send-button"
                aria-label="Send message"
                className="h-10 w-10 rounded-full bg-cobalt text-white flex items-center justify-center disabled:opacity-40 transition-transform duration-200 hover:scale-95"
              >
                <SendHorizonal size={17} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
